import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CandidateRule } from '../dqs.api';
import { useToggleAutoCorrection } from '../hooks/useDqs';
import { LAGEN } from '@xxtract/ui';
import { cn } from '@/lib/utils';

interface AutoCorrectionToggleProps {
  rule: CandidateRule;
}

interface ConfirmDialogProps {
  fieldName: string;
  fromValue: string;
  toValue: string;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialog({ fieldName, fromValue, toValue, onConfirm, onCancel }: ConfirmDialogProps) {
  const { t } = useTranslation();
  return (
    <>
      {/* Backdrop */}
      <div className={cn('fixed inset-0 bg-black/30', LAGEN.venster)} onClick={onCancel} />
      {/* Dialog */}
      <div
        className={cn('fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl p-6', LAGEN.venster)}
        style={{ backgroundColor: 'var(--background)', boxShadow: 'var(--card-shadow-md)' }}
        data-testid="auto-correction-confirm-dialog"
      >
        <h3
          className="mb-3 text-base font-semibold"
          style={{ color: 'var(--foreground-heading)' }}
        >
          {t('dqs.autoCorrection.disableTitle')}
        </h3>
        <p className="mb-5 text-[0.8125rem]" style={{ color: 'var(--foreground)' }}>
          {t('dqs.autoCorrection.disableConfirmation', { fieldName, fromValue, toValue })}
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-lg px-4 py-2 text-[0.8125rem] font-medium"
            style={{ border: '1px solid var(--border)', color: 'var(--foreground)' }}
            data-testid="confirm-cancel"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            className="rounded-lg px-4 py-2 text-[0.8125rem] font-medium text-white"
            style={{ backgroundColor: 'var(--destructive)' }}
            data-testid="confirm-submit"
          >
            {t('common.confirm')}
          </button>
        </div>
      </div>
    </>
  );
}

export function AutoCorrectionToggle({ rule }: AutoCorrectionToggleProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const mutation = useToggleAutoCorrection();
  const ruleId = rule._id || rule.patternKey;
  const isPending = mutation.isPending && mutation.variables?.ruleId === ruleId;

  const handleToggle = () => {
    if (rule.autoCorrectionEnabled) {
      // Disable -> show confirmation dialog
      setShowConfirm(true);
    } else {
      // Enable -> direct
      mutation.mutate({
        ruleId,
        enabled: true,
        fieldName: rule.fieldName,
      });
    }
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    mutation.mutate({
      ruleId,
      enabled: false,
      fieldName: rule.fieldName,
    });
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      {/* Toggle switch */}
      <button
        role="switch"
        aria-checked={rule.autoCorrectionEnabled}
        disabled={isPending}
        onClick={handleToggle}
        className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200"
        style={{
          backgroundColor: rule.autoCorrectionEnabled ? 'var(--success)' : 'var(--border)',
          opacity: isPending ? 0.5 : 1,
          cursor: isPending ? 'not-allowed' : 'pointer',
        }}
        data-testid={`auto-correction-toggle-${ruleId}`}
      >
        <span
          className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform duration-200"
          style={{
            transform: rule.autoCorrectionEnabled ? 'translateX(18px)' : 'translateX(3px)',
          }}
        />
      </button>

      {/* Confirmation dialog */}
      {showConfirm && (
        <ConfirmDialog
          fieldName={rule.fieldName}
          fromValue={rule.exampleFromValue}
          toValue={rule.exampleToValue}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </div>
  );
}
